import { useEffect, useState } from 'react';
import { useDataProvider, useNotify } from 'react-admin';

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog, { DialogProps } from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import GeoStatusAlert from 'components/GeoStatusAlert';
import {
  BumpResourceSheetDto,
  JobPhaseBumpApprovalStatus,
} from '@vatos-pas/common';

import { LocationProvider } from 'providers/locationProvider';

type ConfirmLogModalProps = {
  handleClose: () => void;
  record: BumpResourceSheetDto;
  handleRefetch: () => void;
  title: string;
  confirmLabel: string;
  successMessage: string;
  emphasizeMessage?: boolean;
} & DialogProps;

const ConfirmLogModal = ({
  record,
  handleClose,
  handleRefetch,
  open,
  title,
  confirmLabel,
  successMessage,
  emphasizeMessage = false,
  ...rest
}: ConfirmLogModalProps) => {
  const classes = useStyles();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const {
    coords: geoCoords,
    accuracy: geoAccuracy,
    error: geoError,
    loading: geoLoading,
    requestLocation,
  } = LocationProvider.useContainer();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (open) {
      requestLocation();
    }
  }, [open, requestLocation]);

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const data: Record<string, unknown> = {
        bumperApprovalStatus: JobPhaseBumpApprovalStatus.PENDING_APPROVAL,
      };

      if (geoCoords) {
        data.loggedGeo = {
          type: 'Point',
          coordinates: [geoCoords[0], geoCoords[1]],
        };
        data.loggedGeoAccuracy = geoAccuracy;
      }

      await dataProvider.update('/repair-payment', {
        id: record.id,
        data,
        previousData: record,
      });

      notify(successMessage);
      handleRefetch();
      handleClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to log';
      notify(message, 'warning');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="confirm-log-dialog-title"
      maxWidth="xs"
      fullWidth
      {...rest}
    >
      <DialogTitle
        id="confirm-log-dialog-title"
        className={classes.contentTitle}
      >
        {title}
      </DialogTitle>
      <DialogContent>
        <Typography
          className={emphasizeMessage ? classes.emphasizedMessage : undefined}
        >
          Please ensure that all required work for this task is complete before
          logging.
        </Typography>
        <GeoStatusAlert
          loading={geoLoading}
          error={geoError}
          coords={geoCoords}
        />
      </DialogContent>
      <DialogActions>
        <Box width="100%" display="flex" justifyContent="space-between">
          <Button onClick={handleClose} className={classes.cancelButton}>
            CANCEL
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={20} /> : confirmLabel}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles(theme => ({
  contentTitle: {
    fontWeight: 'bold',
  },
  cancelButton: {
    color: theme.palette.error.main,
  },
  emphasizedMessage: {
    color: theme.palette.error.main,
  },
}));

export default ConfirmLogModal;
